import { MDXContent } from "@content-collections/mdx/react";
import { createFileRoute, Link } from "@tanstack/react-router";

import { allTutorials } from "content-collections";
import { CodeBlock } from "@/components/mdx/CodeBlock";

export const Route = createFileRoute("/_layout/tutorials/$slug")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const tutorial = allTutorials.find((t) => t._meta.path === params.slug);
		if (!tutorial) throw new Error("Tutorial not found");
		return { tutorial };
	},
	head: ({ loaderData }) => {
		const { tutorial } = loaderData;

		// Create breadcrumb schema
		const breadcrumbSchema = {
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			itemListElement: [
				{
					"@type": "ListItem",
					position: 1,
					name: "Home",
					item: "https://macrowave.co",
				},
				{
					"@type": "ListItem",
					position: 2,
					name: "Tutorials",
					item: "https://macrowave.co/tutorials",
				},
				{
					"@type": "ListItem",
					position: 3,
					name: tutorial.title,
					item: `https://macrowave.co/tutorials/${tutorial._meta.path}`,
				},
			],
		};

		// Create HowTo schema for the tutorial
		const howToSchema = {
			"@context": "https://schema.org",
			"@type": "HowTo",
			name: tutorial.seoTitle,
			description: tutorial.summary,
			image: tutorial.image ? `https://macrowave.co${tutorial.image}` : undefined,
			url: `https://macrowave.co/tutorials/${tutorial._meta.path}`,
			datePublished: tutorial.publishedAt,
			dateModified: tutorial.lastModified || tutorial.publishedAt,
			estimatedCost: {
				"@type": "MonetaryAmount",
				currency: "USD",
				value: "0",
			},
			tool: [
				{
					"@type": "HowToTool",
					name: tutorial.platform,
				},
			],
			totalTime: `PT${tutorial.readingTime}M`,
		};

		return {
			links: [
				{
					rel: "canonical",
					href: `https://macrowave.co/tutorials/${tutorial._meta.path}`,
				},
			],
			meta: [
				{
					title: tutorial.seoTitle,
				},
				{
					name: "description",
					content: tutorial.summary,
				},
				{
					property: "og:title",
					content: tutorial.seoTitle,
				},
				{
					property: "og:description",
					content: tutorial.summary,
				},
				{
					property: "og:type",
					content: "article",
				},
				{
					property: "og:url",
					content: `https://macrowave.co/tutorials/${tutorial._meta.path}`,
				},
				...(tutorial.image
					? [
							{
								property: "og:image",
								content: `https://macrowave.co${tutorial.image}`,
							},
					  ]
					: []),
				{
					name: "twitter:card",
					content: "summary_large_image",
				},
				{
					name: "twitter:title",
					content: tutorial.seoTitle,
				},
				{
					name: "twitter:description",
					content: tutorial.summary,
				},
				...(tutorial.image
					? [
							{
								name: "twitter:image",
								content: `https://macrowave.co${tutorial.image}`,
							},
					  ]
					: []),
			],
			scripts: [
				{
					type: "application/ld+json",
					children: JSON.stringify(howToSchema),
				},
				{
					type: "application/ld+json",
					children: JSON.stringify(breadcrumbSchema),
				},
			],
		};
	},
});

function RouteComponent() {
	const { tutorial } = Route.useLoaderData();

	// Get related tutorials (excluding current one)
	const relatedTutorials = allTutorials
		.filter((t) => t._meta.path !== tutorial._meta.path)
		.slice(0, 3);

	return (
		<>
			<article className="max-w-4xl mx-auto py-16 px-6">
				<header className="mb-12">
					<h1 className="text-5xl font-semibold mb-6 tracking-tight leading-tight">
						{tutorial.title}
					</h1>
					<p className="text-xl text-gray-600 leading-relaxed mb-6">
						{tutorial.summary}
					</p>
					<div className="flex items-center gap-2 text-sm text-gray-600">
						<span className="font-medium text-gray-900">
							{tutorial.platform} Guide
						</span>
						<span>·</span>
						<time dateTime={tutorial.publishedAt}>
							Updated{" "}
							{new Date(
								tutorial.lastModified || tutorial.publishedAt
							).toLocaleDateString("en-US", {
								year: "numeric",
								month: "long",
								day: "numeric",
							})}
						</time>
						<span>·</span>
						<span>{tutorial.readingTime} min read</span>
					</div>
				</header>

				{tutorial.image && (
					<div className="mb-12">
						<img
							src={tutorial.image}
							alt={tutorial.title}
							loading="lazy"
							className="w-full rounded-xl"
						/>
					</div>
				)}

				<div className="prose prose-lg prose-gray max-w-none">
					<MDXContent code={tutorial.mdx} components={{ pre: CodeBlock }} />
				</div>

				<footer className="mt-12 pt-8 border-t border-gray-200">
					<div className="flex flex-col gap-6">
						<div>
							<p className="text-sm font-medium text-gray-900 mb-2">
								Ready to get started?
							</p>
							<p className="text-gray-600 mb-4">
								Download the macOS app to start broadcasting, or explore more tutorials.
							</p>
						</div>
						<div className="flex gap-4 text-sm">
							<a
								href="https://apps.apple.com/app/apple-store/id6746954963?pt=123127751&ct=macrowave.co&mt=8"
								target="_blank"
								rel="noopener noreferrer"
								className="text-gray-600 hover:text-gray-900 transition-colors"
							>
								Download App →
							</a>
							<a
								href="/tutorials"
								className="text-gray-600 hover:text-gray-900 transition-colors"
							>
								More Tutorials →
							</a>
						</div>
					</div>
				</footer>
			</article>

			{relatedTutorials.length > 0 && (
				<section className="max-w-7xl mx-auto py-16 px-6 border-t border-gray-200">
					<div className="flex items-center justify-between mb-8">
						<h2 className="font-serif-600 text-3xl text-black">
							Related Tutorials
						</h2>
						<Link
							to="/tutorials"
							className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
						>
							View all →
						</Link>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{relatedTutorials.map((relatedTutorial) => (
							<Link
								key={relatedTutorial._meta.path}
								to={`/tutorials/${relatedTutorial._meta.path}`}
								className="group block"
							>
								<article className="h-full flex flex-col">
									{relatedTutorial.image && (
										<div className="relative aspect-[16/10] mb-4 overflow-hidden rounded-2xl bg-gray-100">
											<img
												src={relatedTutorial.image}
												alt={relatedTutorial.title}
												loading="lazy"
												className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
											/>
										</div>
									)}
									<div className="flex flex-col flex-grow">
										<div className="flex items-center justify-between mb-2">
											<span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
												{relatedTutorial.platform}
											</span>
											<span className="font-serif text-xs text-gray-500">
												{relatedTutorial.readingTime} min read
											</span>
										</div>
										<h3 className="font-serif-600 text-xl text-black mb-2 leading-tight group-hover:text-gray-600 transition-colors">
											{relatedTutorial.title}
										</h3>
										<p className="font-serif text-gray-600 leading-relaxed text-sm flex-grow">
											{relatedTutorial.summary}
										</p>
									</div>
								</article>
							</Link>
						))}
					</div>
				</section>
			)}
		</>
	);
}
